import React from 'react';

import { Stack, Tooltip } from '@mui/material';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { validateVerificationCode } from '../../../../api/security';
import { useAuth } from '../../../../hooks/useAuth';
import {
  ButtonStyled,
  FormTextLabelStyled,
  TextFieldStyled,
  TooltipSx,
  TypographyInfoStyled,
} from '../../styles';
import { IValidationCodeForm, IVerificationCodeForm } from '../../types';

interface IProps {
  phone?: string | null;
  email?: string | null;
}

const VerificationCodeForm = React.memo(({ phone, email }: IProps) => {
  const { t } = useTranslation();

  const [isProcess, setProcess] = React.useState<boolean>(false);

  const { authorize } = useAuth();

  const onSubmit = React.useCallback(
    async (values: IVerificationCodeForm) => {
      if (!phone && !email) {
        return;
      }

      setProcess(true);

      try {
        const payload: IValidationCodeForm = phone
          ? { phone, code: values.code }
          : { email: email!, code: values.code };

        const response = await validateVerificationCode(payload);

        await authorize(response.access_token);
      } finally {
        setProcess(false);
      }
    },
    [authorize, email, phone],
  );

  return (
    <Formik
      initialValues={{
        code: '',
      }}
      validationSchema={Yup.object({
        code: Yup.string().required(t('signin.code.form_error')),
      })}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Form>
          <Stack spacing={3} alignItems="center">
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <Stack spacing={1}>
                <FormTextLabelStyled htmlFor="contact">
                  {t(`general.${phone ? 'phone' : 'email'}`)}
                </FormTextLabelStyled>

                <TextFieldStyled
                  variant="outlined"
                  autoComplete="off"
                  value={phone || email || ''}
                  id="contact"
                  name="contact"
                />
              </Stack>

              <Stack spacing={1}>
                <FormTextLabelStyled htmlFor="code">{t('signin.code.code')}</FormTextLabelStyled>

                <Tooltip
                  title={formik.errors.code}
                  open={!!formik.errors.code}
                  arrow
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: TooltipSx,
                    },
                  }}
                >
                  <TextFieldStyled
                    variant="outlined"
                    autoComplete="off"
                    {...formik.getFieldProps('code')}
                    error={formik.touched.code && Boolean(formik.errors.code)}
                    id="code"
                  />
                </Tooltip>
              </Stack>

              <ButtonStyled
                variant="contained"
                color="primary"
                disabled={!formik.dirty || !formik.isValid || isProcess}
                onClick={() => formik.handleSubmit()}
              >
                {t('signin.code.send')}
              </ButtonStyled>
            </Stack>

            <Stack width="100%" alignItems="center">
              <TypographyInfoStyled>
                {t(`signin.${phone ? 'phone' : 'email'}.info`)}
              </TypographyInfoStyled>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
});

export default VerificationCodeForm;
